import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import throttle from 'lodash/throttle';
import { useFormContext } from 'react-hook-form';

export { useLocalStorage } from './useLocalStorage';
export { useSessionStorage } from './useSessionStorage';

export const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

export const useRefInView = (initialState = false) => {
  const [refInView, setRefInView] = useState(initialState);
  const triggerRef = useRef(null);

  useEffect(() => {
    const onViewportChange = () => {
      if (triggerRef.current) {
        const { top, bottom } = triggerRef.current.getBoundingClientRect();

        if (top >= 0 && bottom <= window.innerHeight) {
          setRefInView(true);
        } else {
          setRefInView(false);
        }
      }
    };

    const throttledOnViewportChange = throttle(onViewportChange, 100);
    onViewportChange();
    window.addEventListener('scroll', throttledOnViewportChange);
    window.addEventListener('resize', throttledOnViewportChange);
    return () => {
      window.removeEventListener('scroll', throttledOnViewportChange);
      window.removeEventListener('resize', throttledOnViewportChange);
    };
  }, []);

  return [refInView, triggerRef];
};

export const useResizeWindow = () => {
  let hasWindow = false;
  if (typeof window !== 'undefined') {
    hasWindow = true;
  }
  const [width, setWidth] = useState(hasWindow ? window.innerWidth : null);

  useEffect(() => {
    const handleResizeWindow = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResizeWindow);
    return () => window.removeEventListener('resize', handleResizeWindow);
  }, []);

  return { width };
};

export const useLoaded = () => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => setLoaded(true), []);
  return loaded;
};

export const useKeyboardEvent = (key, callback) => {
  useEffect(() => {
    const handler = event => {
      if (event.key === key) {
        callback();
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [key, callback]);
};

export const useSubFormIsValidSync = schema => {
  const { watch } = useFormContext();

  const subFormFields = Object.keys(schema.fields);
  const subFormState = watch(subFormFields).reduce((acc, cur, i) => {
    acc[subFormFields[i]] = cur;
    return acc;
  }, {});

  return schema.isValidSync(subFormState);
};
